// @ts-nocheck
import { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  FormHelperText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { FilePdf, FileDoc, FileXls } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { XGButton } from 'xg-cl';
import { useTranslation } from 'react-i18next';
import { getLineItemAttachmentUrl } from '../../../utils/resourcePathHelper';
import { isFileTypeAndExtensionAllowed } from '../../../utils/fileValidation';
import * as PropTypes from 'prop-types';

const getFileTypeIcon = (fileName) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  const iconProps = { size: 20, style: { color: 'var(--gray-500)' } };

  switch (ext) {
    case 'pdf':
      return <FilePdf {...iconProps} />;
    case 'doc':
    case 'docx':
      return <FileDoc {...iconProps} />;
    case 'xls':
    case 'xlsx':
      return <FileXls {...iconProps} />;
    default:
      return (
        <AttachFileIcon fontSize='small' sx={{ color: 'var(--gray-500)' }} />
      );
  }
};

/**
 * LineItemAttachmentManager - Custom attachment manager that handles both existing attachments and new uploads
 *
 * This component:
 * 1. Displays existing attachments (with resourcepath) as download links
 * 2. Handles new attachment uploads (File objects)
 * 3. Maintains the same UI/UX as TripDetailedDayServices
 */
const LineItemAttachmentManager = ({
  attachments = [],
  onChange,
  onError,
  disabled = false,
  maxAttachments = 1,
  maxSize = 10 * 1024 * 1024, // 10MB default
  label = '',
  uploadLabel = '',
  accept = '',
  acceptedMimeTypes = [],
  acceptedExtensions = [],
  error = '',
  hint = '',
  previouslyProvided = false,
  previouslyProvidedLabel = '',
  existingAttachments = [],
  existingAttachmentsTitle = '',
  addMoreFilesTitle = '',
  existingAttachmentBadge = '',
  showExistingAttachmentDates = false,
  showInlineHint = false
}) => {
  const { t } = useTranslation(['common']);
  const [showUploadArea, setShowUploadArea] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [internalError, setInternalError] = useState('');
  const fileInputRef = useRef(null);
  const displayedError = error || internalError;
  const existingAttachmentCount = existingAttachments.length;
  const availableAttachmentSlots = Math.max(
    maxAttachments - existingAttachmentCount,
    0
  );
  const hasReachedMax = attachments.length >= availableAttachmentSlots;
  const maxSizeInMb = Math.floor(maxSize / (1024 * 1024));
  const buttonLabel = label || t('addAttachment');
  const uploadButtonLabel = uploadLabel || t('uploadAttachment');
  const providedLabel = previouslyProvidedLabel || t('previouslyProvided');

  const clearError = () => {
    setInternalError('');
    onError?.('');
  };

  const setError = (message = '') => {
    setInternalError(message);
    onError?.(message);
  };

  const handleAddClick = () => {
    clearError();
    setShowUploadArea(true);
  };

  const handleFileSelect = (event) => {
    const newFiles = Array.from(event.target.files);
    processFiles(newFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (disabled || hasReachedMax || previouslyProvided) {
      return;
    }
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || hasReachedMax || previouslyProvided) {
      return;
    }
    const newFiles = Array.from(e.dataTransfer.files);
    processFiles(newFiles);
  };

  const isAcceptedFileType = (file) => {
    return isFileTypeAndExtensionAllowed(
      file,
      acceptedMimeTypes,
      acceptedExtensions
    );
  };

  const processFiles = (newFiles) => {
    if (!newFiles.length) {
      return;
    }

    const validFiles = [];
    let validationError = '';

    newFiles.forEach((file) => {
      if (file.size > maxSize) {
        if (!validationError) {
          validationError = t('fileTooLarge', { maxFileSize: maxSizeInMb });
        }
        return;
      }

      if (!isAcceptedFileType(file)) {
        if (!validationError) {
          validationError = t('unsupportedFileType');
        }
        return;
      }

      validFiles.push(file);
    });

    if (attachments.length + validFiles.length > availableAttachmentSlots) {
      setError(t('maxAttachmentsReached', { maxAttachments }));
      return;
    }

    if (validFiles.length > 0) {
      onChange([...attachments, ...validFiles]);
      clearError();
      setShowUploadArea(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }

    if (validationError) {
      setError(validationError);
    } else {
      clearError();
    }
  };

  const handleDelete = (index) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    onChange(newAttachments);
    clearError();
  };

  const getAttachmentUrl = (attachment) => {
    // If it's a database record with resourcepath, use the helper
    if (attachment.resourcepath) {
      return getLineItemAttachmentUrl({
        resourcePath: attachment.resourcepath
      });
    }
    return null;
  };

  const getAttachmentName = (attachment) => {
    if (attachment instanceof File) {
      return attachment.name;
    }
    return (
      attachment.attachmentname ||
      attachment.originalFilename ||
      attachment.originalfilename ||
      attachment.filename ||
      attachment.originalname ||
      attachment.name ||
      attachment.relativePath?.split('/').pop() ||
      attachment.resourcepath?.split('/').pop() ||
      'Attachment'
    );
  };

  const getAttachmentUploadedLabel = (attachment) => {
    if (!showExistingAttachmentDates) {
      return '';
    }

    const uploadedAt =
      attachment.uploadedAt || attachment.uploadedat || attachment.createdat;

    if (!uploadedAt) {
      return '';
    }

    const uploadedDate = new Date(uploadedAt);

    if (Number.isNaN(uploadedDate.getTime())) {
      return '';
    }

    return t('uploadedDate', {
      date: format(uploadedDate, 'MMM d, yyyy')
    });
  };

  return (
    <Box>
      {existingAttachments.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginBottom: '12px'
          }}
        >
          {existingAttachmentsTitle && (
            <Typography
              variant='body2'
              sx={{
                color: 'var(--gray-900)',
                fontWeight: 600,
                lineHeight: '20px'
              }}
            >
              {existingAttachmentsTitle}
            </Typography>
          )}
          {existingAttachments.map((attachment, index) => {
            const name = getAttachmentName(attachment);
            const uploadedLabel = getAttachmentUploadedLabel(attachment);
            const showBadge = attachment?.showBadge || false;

            return (
              <Box
                key={`${name}-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 12px',
                  backgroundColor: '#f9fafb',
                  border: '1px solid var(--gray-200)',
                  borderRadius: '8px',
                  gap: '10px'
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    minWidth: 34,
                    borderRadius: '6px',
                    backgroundColor: 'var(--gray-100)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {getFileTypeIcon(name)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Tooltip title={name}>
                    <Typography
                      variant='body2'
                      sx={{
                        color: 'var(--gray-900)',
                        lineHeight: '18px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {name}
                    </Typography>
                  </Tooltip>
                  {uploadedLabel && (
                    <Typography
                      variant='caption'
                      sx={{ color: 'var(--gray-500)', lineHeight: '16px' }}
                    >
                      {uploadedLabel}
                    </Typography>
                  )}
                </Box>
                {existingAttachmentBadge && showBadge && (
                  <Typography
                    variant='caption'
                    sx={{
                      color: 'var(--gray-600)',
                      backgroundColor: 'var(--gray-100)',
                      border: '1px solid var(--gray-200)',
                      borderRadius: '4px',
                      padding: '2px 8px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {existingAttachmentBadge}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {previouslyProvided && existingAttachments.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            gap: '8px'
          }}
        >
          <AttachFileIcon fontSize='small' sx={{ color: 'var(--gray-500)' }} />
          <Typography variant='body2'>{providedLabel}</Typography>
        </Box>
      )}

      {/* Add Attachment Button */}
      {!previouslyProvided &&
        !showUploadArea &&
        availableAttachmentSlots > 0 && (
          <Box
            sx={
              existingAttachments.length > 0
                ? {
                    borderTop: '1px solid var(--gray-200)',
                    paddingTop: '14px',
                    marginTop: '14px'
                  }
                : undefined
            }
          >
            {addMoreFilesTitle && existingAttachments.length > 0 && (
              <Typography
                variant='body2'
                sx={{
                  color: 'var(--gray-900)',
                  fontWeight: 600,
                  lineHeight: '20px',
                  marginBottom: '8px'
                }}
              >
                {addMoreFilesTitle}
              </Typography>
            )}
            <XGButton
              id='add-attachment-button'
              text={buttonLabel}
              type='secondary'
              size='large'
              variant='contained'
              onClick={handleAddClick}
              startIcon={<AddCircleIcon />}
              disabled={disabled || hasReachedMax}
            />
            {showInlineHint && hint && (
              <Typography
                variant='caption'
                display='block'
                sx={{ mt: 1, color: 'var(--gray-500)' }}
              >
                {hint}
              </Typography>
            )}
          </Box>
        )}

      {/* Upload Area */}
      {!previouslyProvided &&
        showUploadArea &&
        availableAttachmentSlots > 0 && (
          <Box
            role='button'
            tabIndex={disabled || hasReachedMax ? -1 : 0}
            aria-label={uploadButtonLabel}
            sx={{
              border: isDragging
                ? '2px dashed var(--primary-600)'
                : '2px dashed #ccc',
              borderRadius: '8px',
              padding: '32px',
              textAlign: 'center',
              backgroundColor: isDragging ? '#f0f7ff' : '#fafafa',
              mb: 2,
              cursor: disabled || hasReachedMax ? 'not-allowed' : 'pointer'
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => {
              if (!disabled && !hasReachedMax) {
                fileInputRef.current?.click();
              }
            }}
            onKeyDown={(event) => {
              if (
                !disabled &&
                !hasReachedMax &&
                (event.key === 'Enter' || event.key === ' ')
              ) {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <input
              ref={fileInputRef}
              type='file'
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              disabled={disabled || hasReachedMax}
              accept={accept}
            />
            <Typography variant='body1' sx={{ mb: 1 }}>
              {t('dragAndDropOr')}
            </Typography>
            <XGButton
              text={uploadButtonLabel}
              type='primary'
              size='medium'
              variant='contained'
              disabled={disabled || hasReachedMax}
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            />
            <Typography
              variant='caption'
              display='block'
              sx={{ mt: 2, color: 'var(--gray-500)' }}
            >
              {hint || t('maxFileSize', { maxFileSize: maxSizeInMb })}
            </Typography>
          </Box>
        )}

      {/* Attachment List */}
      {!previouslyProvided && attachments.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginTop: '16px'
          }}
        >
          {attachments.map((attachment, index) => {
            const name = getAttachmentName(attachment);
            const url = getAttachmentUrl(attachment);
            const isFile = attachment instanceof File;

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  gap: '8px'
                }}
              >
                {getFileTypeIcon(name)}
                <Tooltip title={name}>
                  <Typography
                    variant='body2'
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {isFile ? (
                      name
                    ) : (
                      <a
                        href={url}
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{
                          color: 'var(--primary-600)',
                          textDecoration: 'none'
                        }}
                      >
                        {name}
                      </a>
                    )}
                  </Typography>
                </Tooltip>
                {!disabled && (
                  <Tooltip title={t('delete')}>
                    <IconButton
                      size='small'
                      aria-label={t('removeFile', { fileName: name })}
                      onClick={() => handleDelete(index)}
                    >
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {displayedError && (
        <FormHelperText className='input-form-helper-text--error'>
          {displayedError}
        </FormHelperText>
      )}
    </Box>
  );
};

LineItemAttachmentManager.propTypes = {
  attachments: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  disabled: PropTypes.bool,
  maxAttachments: PropTypes.number,
  maxSize: PropTypes.number,
  label: PropTypes.string,
  uploadLabel: PropTypes.string,
  accept: PropTypes.string,
  acceptedMimeTypes: PropTypes.array,
  acceptedExtensions: PropTypes.array,
  error: PropTypes.string,
  hint: PropTypes.string,
  previouslyProvided: PropTypes.bool,
  previouslyProvidedLabel: PropTypes.string,
  existingAttachments: PropTypes.array,
  existingAttachmentsTitle: PropTypes.string,
  addMoreFilesTitle: PropTypes.string,
  existingAttachmentBadge: PropTypes.string,
  showExistingAttachmentDates: PropTypes.bool,
  showInlineHint: PropTypes.bool
};

export default LineItemAttachmentManager;
